import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Calendar, Trophy, UserPlus, Star, Users, BarChart3 } from 'lucide-react';

const PublicPlayerStats = () => {
  const { userId } = useParams();
  const [player, setPlayer] = useState(null);
  const [games, setGames] = useState([]);
  const [seasonStats, setSeasonStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const calculateSkaterStats = useCallback((games) => {
    const totalGames = games.length;
    const goals = games.reduce((sum, game) => sum + (game.goals || 0), 0);
    const assists = games.reduce((sum, game) => sum + (game.assists || 0), 0);
    const points = goals + assists;

    const wins = games.filter(game => game.our_score > game.their_score).length;
    const losses = games.filter(game => game.our_score < game.their_score).length;
    const ties = games.filter(game => game.our_score === game.their_score).length;

    return {
      totalGames,
      goals,
      assists,
      points,
      wins,
      losses,
      ties,
      goalsAgainst: 0,
      saves: 0,
      shotsAgainst: 0,
      savePercentage: 0,
      goalsAgainstAverage: 0,
      shutouts: 0,
      gamesStarted: 0,
      minutesPlayed: 0
    };
  }, []);

  const calculateGoalieStats = useCallback((games) => {
    const totalGames = games.length;
    const goalsAgainst = games.reduce((sum, game) => sum + (game.goals_against || 0), 0);
    const saves = games.reduce((sum, game) => sum + (game.saves || 0), 0);
    const shotsAgainst = games.reduce((sum, game) => sum + (game.shots_against || 0), 0);
    const savePercentage = shotsAgainst > 0 ? ((saves / shotsAgainst) * 100).toFixed(1) : 0;

    const totalMinutes = games.reduce((sum, game) => sum + (game.minutes_played || 0), 0);
    const goalsAgainstAverage = totalMinutes > 0 ? ((goalsAgainst / totalMinutes) * 60).toFixed(2) : 0;

    const shutouts = games.filter(game => game.goals_against === 0).length;
    const gamesStarted = games.filter(game => game.started_game).length;

    const wins = games.filter(game => game.our_score > game.their_score).length;
    const losses = games.filter(game => game.our_score < game.their_score).length;
    const ties = games.filter(game => game.our_score === game.their_score).length;

    return {
      totalGames,
      goals: 0,
      assists: 0,
      points: 0,
      wins,
      losses,
      ties,
      goalsAgainst,
      saves,
      shotsAgainst,
      savePercentage,
      goalsAgainstAverage,
      shutouts,
      gamesStarted,
      minutesPlayed: totalMinutes
    };
  }, []);

  const calculateSeasonStats = useCallback((games, position) => {
    if (!games || games.length === 0) {
      return {
        totalGames: 0,
        goals: 0,
        assists: 0,
        points: 0,
        wins: 0,
        losses: 0,
        ties: 0,
        goalsAgainst: 0,
        saves: 0,
        shotsAgainst: 0,
        savePercentage: 0,
        goalsAgainstAverage: 0,
        shutouts: 0,
        gamesStarted: 0,
        minutesPlayed: 0
      };
    }

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();

    // Determine current season (June to May)
    let seasonStart, seasonEnd;
    if (currentMonth < 5) { // Before June
      seasonStart = new Date(currentYear - 1, 5, 1); // June 1st last year
      seasonEnd = new Date(currentYear, 4, 31, 23, 59, 59, 999); // May 31st this year
    } else {
      seasonStart = new Date(currentYear, 5, 1); // June 1st this year
      seasonEnd = new Date(currentYear + 1, 4, 31, 23, 59, 59, 999); // May 31st next year
    }

    // Filter games for current season
    const seasonGames = games.filter(game => {
      const gameDate = new Date(game.date);
      return gameDate >= seasonStart && gameDate <= seasonEnd;
    });

    if (position === 'goalie') {
      return calculateGoalieStats(seasonGames);
    } else {
      return calculateSkaterStats(seasonGames);
    }
  }, [calculateGoalieStats, calculateSkaterStats]);

  const fetchPlayerData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch player info
      const playerResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/users/public/${userId}`);
      if (!playerResponse.ok) {
        throw new Error('Player not found');
      }
      const playerData = await playerResponse.json();
      setPlayer(playerData);

      // Fetch player games
      const gamesResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/games/public/user/${userId}`);
      if (!gamesResponse.ok) {
        throw new Error('Failed to fetch games');
      }
      const gamesData = await gamesResponse.json();
      setGames(gamesData.games || []);

      // Calculate season stats
      const stats = calculateSeasonStats(gamesData.games || [], playerData.position);
      setSeasonStats(stats);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, calculateSeasonStats]);

  useEffect(() => {
    fetchPlayerData();
    
    // Check if user is logged in by looking for auth token
    const checkAuthStatus = () => {
      const token = localStorage.getItem('access_token');
      setIsLoggedIn(!!token);
    };
    
    checkAuthStatus();
  }, [fetchPlayerData]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading player stats...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Player Not Found</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-600 text-6xl mb-4">👤</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Player Not Found</h1>
          <p className="text-gray-600">This player profile is not available.</p>
        </div>
      </div>
    );
  }

  const isGoalie = player.position === 'goalie';

  return (
    <>
      <Helmet>
        <title>{player.first_name} {player.last_name} - Hockey Stats</title>
        <meta name="description" content={`View ${player.first_name} ${player.last_name}'s current hockey season statistics and game history.`} />
        <meta property="og:title" content={`${player.first_name} ${player.last_name} - Hockey Stats`} />
        <meta property="og:description" content={`View ${player.first_name} ${player.last_name}'s current hockey season statistics and game history.`} />
        <meta property="og:type" content="profile" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${player.first_name} ${player.last_name} - Hockey Stats`} />
        <meta name="twitter:description" content={`View ${player.first_name} ${player.last_name}'s current hockey season statistics and game history.`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 py-6">
            <div className="flex items-center space-x-4">
              {player.avatar ? (
                <img
                  src={player.avatar}
                  alt={`${player.first_name} ${player.last_name}`}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-gray-600">
                    {player.first_name?.[0]}{player.last_name?.[0]}
                  </span>
                </div>
              )}
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {player.first_name} {player.last_name}
                </h1>
                <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                  <span className="flex items-center">
                    <Trophy className="w-4 h-4 mr-1" />
                    {player.level || 'Player'}
                  </span>
                  {player.age && (
                    <span>Age {player.age}</span>
                  )}
                  <span className="capitalize">{player.position || 'Player'}</span>
                </div>
                {player.organization && (
                  <div className="mt-2 flex items-center space-x-2">
                    {player.organization.image_url && (
                      <img
                        src={player.organization.image_url}
                        alt="Organization"
                        className="w-4 h-4 rounded object-contain"
                      />
                    )}
                    <span className="text-sm text-gray-500">
                      {player.organization.team_name || player.organization.name}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Season Stats */}
          {seasonStats && (
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Current Season Stats</h2>
              
              {isGoalie ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{seasonStats.totalGames}</div>
                    <div className="text-sm text-gray-600">Games Played</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{seasonStats.gamesStarted}</div>
                    <div className="text-sm text-gray-600">Games Started</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{seasonStats.minutesPlayed}</div>
                    <div className="text-sm text-gray-600">Minutes Played</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{seasonStats.saves}</div>
                    <div className="text-sm text-gray-600">Saves</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{seasonStats.shotsAgainst}</div>
                    <div className="text-sm text-gray-600">Shots Against</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{seasonStats.savePercentage}%</div>
                    <div className="text-sm text-gray-600">Save %</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{seasonStats.goalsAgainst}</div>
                    <div className="text-sm text-gray-600">Goals Against</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{seasonStats.goalsAgainstAverage}</div>
                    <div className="text-sm text-gray-600">GAA</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{seasonStats.shutouts}</div>
                    <div className="text-sm text-gray-600">Shutouts</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{seasonStats.wins}</div>
                    <div className="text-sm text-gray-600">Wins</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-600">{seasonStats.losses}</div>
                    <div className="text-sm text-gray-600">Losses</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-yellow-600">{seasonStats.ties}</div>
                    <div className="text-sm text-gray-600">Ties</div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{seasonStats.totalGames}</div>
                    <div className="text-sm text-gray-600">Games Played</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{seasonStats.goals}</div>
                    <div className="text-sm text-gray-600">Goals</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{seasonStats.assists}</div>
                    <div className="text-sm text-gray-600">Assists</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{seasonStats.points}</div>
                    <div className="text-sm text-gray-600">Points</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{seasonStats.wins}</div>
                    <div className="text-sm text-gray-600">Wins</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-600">{seasonStats.losses}</div>
                    <div className="text-sm text-gray-600">Losses</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-yellow-600">{seasonStats.ties}</div>
                    <div className="text-sm text-gray-600">Ties</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Games List */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="px-6 py-4 border-b">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                <Calendar className="w-6 h-6 mr-2" />
                Games This Season
              </h2>
            </div>
            
            {games.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No games recorded this season</p>
              </div>
            ) : (
              <div className="divide-y">
                {games.map((game) => (
                  <div key={game.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      {/* Left side - Result indicator */}
                      <div className="flex-shrink-0 mr-4">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                          game.our_score > game.their_score ? 'bg-green-500' : 
                          game.our_score < game.their_score ? 'bg-red-500' : 'bg-yellow-500'
                        }`}>
                          {game.our_score > game.their_score ? 'W' : 
                           game.our_score < game.their_score ? 'L' : 'T'}
                        </span>
                      </div>

                      {/* Center - Teams and Score */}
                      <div className="flex-1">
                        <div className="flex items-center justify-center space-x-6">
                          {/* Our Team */}
                          <div className="flex items-center space-x-2">
                            {game.userOrganization?.image_url && (
                              <img
                                src={game.userOrganization.image_url}
                                alt="Team"
                                className="w-8 h-8 rounded object-contain"
                              />
                            )}
                            <div className="text-center">
                              <div className="text-sm font-medium text-gray-900">
                                {game.userOrganization?.team_name || game.userOrganization?.name || 'Our Team'}
                              </div>
                            </div>
                          </div>

                          {/* Score */}
                          <div className="text-center">
                            <div className="text-3xl font-bold text-gray-900">
                              {game.our_score} - {game.their_score}
                            </div>
                            <div className="mt-1">
                              <span className="text-sm text-gray-500">{formatDate(game.date)}</span>
                            </div>
                          </div>

                          {/* Opponent Team */}
                          <div className="flex items-center space-x-2">
                            <div className="text-center">
                              <div className="text-sm font-medium text-gray-900">
                                {game.opponent?.team_name || game.opponent?.name || 'Opponent'}
                              </div>
                            </div>
                            {game.opponent?.image_url && (
                              <img
                                src={game.opponent.image_url}
                                alt="Opponent"
                                className="w-8 h-8 rounded object-contain"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Right side - Player Stats */}
                      <div className="ml-6 text-right">
                        {isGoalie ? (
                          <div className="space-y-1">
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">SV:</span> {game.saves || 0}
                            </div>
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">GA:</span> {game.goals_against || 0}
                            </div>
                            {game.started_game && (
                              <div className="text-xs text-blue-600 font-medium">Started</div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">G:</span> {game.goals || 0}
                            </div>
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">A:</span> {game.assists || 0}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer - Only show for non-logged-in users */}
        {!isLoggedIn && (
          <div className="mt-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center mb-6">
                <div className="bg-white/20 rounded-full p-3 mr-4">
                  <Trophy className="w-8 h-8" />
                </div>
                <h2 className="text-3xl font-bold">Join Biscuit Hockey</h2>
              </div>
              
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                Track your hockey stats, share highlights, and connect with your team. 
                See how {player?.first_name} {player?.last_name} is doing this season!
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white/10 rounded-lg p-6">
                  <BarChart3 className="w-8 h-8 mx-auto mb-4 text-blue-200" />
                  <h3 className="text-lg font-semibold mb-2">Track Your Stats</h3>
                  <p className="text-blue-100 text-sm">
                    Record goals, assists, saves, and more. See your progress throughout the season.
                  </p>
                </div>
                
                <div className="bg-white/10 rounded-lg p-6">
                  <Users className="w-8 h-8 mx-auto mb-4 text-blue-200" />
                  <h3 className="text-lg font-semibold mb-2">Connect with Team</h3>
                  <p className="text-blue-100 text-sm">
                    Follow teammates, share highlights, and stay connected with your hockey community.
                  </p>
                </div>
                
                <div className="bg-white/10 rounded-lg p-6">
                  <Star className="w-8 h-8 mx-auto mb-4 text-blue-200" />
                  <h3 className="text-lg font-semibold mb-2">Share Highlights</h3>
                  <p className="text-blue-100 text-sm">
                    Upload and share your best moments. Let others see your skills on the ice.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/login"
                  className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center"
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  Create Free Account
                </Link>
                <Link
                  to="/"
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PublicPlayerStats;
